import React, { useEffect, useState } from 'react';
import { T, serif, Boton } from '../lib/ui';

// ════════════════════════════════════════════════════════════════════
// Invitación a instalar
//
// La plataforma ya se puede instalar en el celular y queda como una
// app de verdad: ícono propio, pantalla completa, y funciona sin señal
// para capturar en obra.
//
// El problema no era técnico: nadie se lo decía al usuario. Esto lo
// ofrece una vez, y si dicen que no, no vuelve a molestar.
//
// Android y iPhone se instalan distinto:
//   Android/Chrome  el navegador avisa y el sistema lo instala solo
//   iPhone/Safari   no hay aviso: hay que explicar los dos toques
// ════════════════════════════════════════════════════════════════════

const CLAVE_DESCARTE = 'rinde:instalacion-descartada';

/** ¿Ya está corriendo instalada? */
export function estaInstalada() {
  return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}

const esIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const esSafari = () =>
  /safari/i.test(navigator.userAgent) && !/chrome|crios|fxios|edgios/i.test(navigator.userAgent);

export function InvitacionInstalar() {
  const [evento, setEvento] = useState(null);
  const [visible, setVisible] = useState(false);
  const [instalando, setInstalando] = useState(false);

  useEffect(() => {
    if (estaInstalada()) return undefined;
    if (localStorage.getItem(CLAVE_DESCARTE)) return undefined;

    // Android: el navegador avisa cuando la considera instalable
    const alPoder = (e) => {
      e.preventDefault();
      setEvento(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', alPoder);

    // iPhone: no existe ese aviso, así que se muestra la explicación
    // después de un rato, para no interrumpir apenas entran.
    //
    // Antes esto exigía Safari, y quien abría el enlace desde Chrome o
    // desde un enlace de WhatsApp no veía absolutamente nada. Ahora se
    // muestra siempre en iPhone: si no está en Safari, lo primero que
    // dice es que hay que abrirlo ahí.
    let temporizador;
    if (esIOS()) {
      temporizador = setTimeout(() => setVisible(true), 3500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', alPoder);
      clearTimeout(temporizador);
    };
  }, []);

  const cerrar = (paraSiempre) => {
    setVisible(false);
    if (paraSiempre) localStorage.setItem(CLAVE_DESCARTE, '1');
  };

  const instalar = async () => {
    if (!evento) return;
    setInstalando(true);
    evento.prompt();
    const r = await evento.userChoice;
    setInstalando(false);
    // Si dijo que no, no se vuelve a preguntar
    cerrar(r.outcome !== 'accepted');
  };

  if (!visible) return null;

  const enIphone = esIOS();

  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 130,
      background: 'rgba(10,15,26,.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={() => cerrar(false)}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: '14px 14px 0 0',
        width: '100%', maxWidth: 480, padding: '20px 18px 26px',
      }}>
        <div style={{ display: 'flex', gap: 13, alignItems: 'center', marginBottom: 14 }}>
          <img src="/icon-192.png" alt=""
               style={{ width: 46, height: 46, borderRadius: 10, flexShrink: 0 }} />
          <div>
            <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 600, color: T.navy }}>
              Instálala en tu celular
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
              Queda como una app, y funciona sin señal
            </div>
          </div>
        </div>

        <div style={{ fontSize: 13, color: T.tinta, lineHeight: 1.6, marginBottom: 16 }}>
          Instalada abre directo, sin buscar la dirección ni entrar cada vez.
          En obra puedes capturar gastos aunque no haya señal: se guardan en el
          teléfono y suben solos al recuperar cobertura.
        </div>

        {enIphone ? (
          <>
            {!esSafari() && (
              <div style={{
                background: '#FBF0DC', borderLeft: '3px solid #B8761C',
                borderRadius: 8, padding: 13, marginBottom: 14,
                fontSize: 13, lineHeight: 1.55,
              }}>
                <strong>Estás en otro navegador.</strong> En iPhone solo Safari
                puede instalar la aplicación. Copia la dirección, ábrela en
                Safari y sigue estos pasos.
              </div>
            )}

            <div style={{
              background: T.warm, borderRadius: 9, padding: 14, marginBottom: 16,
            }}>
              {[
                ['1', 'Toca el botón de compartir', 'El cuadrado con la flecha hacia arriba, abajo en la barra de Safari.'],
                ['2', 'Elige "Añadir a pantalla de inicio"', 'Hay que bajar un poco en la lista.'],
                ['3', 'Toca "Añadir"', 'El ícono queda junto a tus otras apps.'],
              ].map(([n, tit, det]) => (
                <div key={n} style={{ display: 'flex', gap: 11, marginBottom: 12 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    background: T.navy, color: T.brass, fontSize: 12, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {n}
                  </span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{tit}</div>
                    <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>{det}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <Boton variante="suave" onClick={() => cerrar(true)} style={{ flex: 1 }}>
                No mostrar más
              </Boton>
              <Boton variante="acento" onClick={() => cerrar(false)} style={{ flex: 1 }}>
                Entendido
              </Boton>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Boton variante="suave" onClick={() => cerrar(true)} style={{ flex: 1 }}>
              Ahora no
            </Boton>
            <Boton variante="acento" onClick={instalar}
                   disabled={instalando} style={{ flex: 1.5 }}>
              {instalando ? 'Instalando…' : 'Instalar'}
            </Boton>
          </div>
        )}
      </div>
    </div>
  );
}
